/* ──────────────────────────────────────────────────────────────────────────
 * Data trust & provenance (brief §9, §14).
 *
 * The rule this file exists to enforce: nothing fabricated is ever presented
 * as observed. Every operational object carries where it came from, when, how
 * confident we are, and whether a human verified it — and the UI is built so
 * that an object with no provenance cannot render as if it were verified.
 *
 * This is not decoration. In an operational government context the difference
 * between "92,140 people at Ramkund" as a sensor reading and as a simulation
 * output is the difference between a defensible decision and an indefensible
 * one. The POC has no sensors, so almost everything here is SIMULATED, and it
 * says so on every object and in every panel that renders one.
 * ────────────────────────────────────────────────────────────────────────── */

/* Verification status. Ordered weakest to strongest claim. */
export const TRUST = {
  SIMULATED: "SIMULATED",   // generated for demo. Not observed. Never operational.
  UNVERIFIED: "UNVERIFIED", // came from a real source, nobody has confirmed it
  STALE: "STALE",           // was verified, but older than its freshness window
  VERIFIED: "VERIFIED",     // confirmed by a named authority within its window
};

/* How an object entered the system. Mirrors the ingestion adapters in §10 —
 * the platform must not assume a government database is available. */
export const SOURCE_KIND = {
  OSM: "OpenStreetMap",
  OPEN_DATA: "Open dataset",
  GEOJSON: "GeoJSON import",
  CSV: "CSV import",
  OPERATOR: "Manual operator entry",
  SENSOR: "Authorised sensor feed",
  CCTV: "Authorised CCTV analytics",
  DEPARTMENT: "Department-provided file",
  API: "Approved API",
  SIMULATION: "InQAi simulation",
};

/* Data classification, so sensitive layers can be permission-gated (§11). */
export const CLASSIFICATION = {
  PUBLIC: "PUBLIC",
  INTERNAL: "INTERNAL",
  RESTRICTED: "RESTRICTED", // e.g. police deployment, resource positions
};

/* Presentation for each trust level. Kept next to the definitions so a new
 * status cannot be added without deciding how it looks. */
export const TRUST_STYLE = {
  [TRUST.VERIFIED]:   { label: "VERIFIED",   cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  [TRUST.UNVERIFIED]: { label: "UNVERIFIED", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  [TRUST.STALE]:      { label: "STALE",      cls: "bg-slate-200 text-slate-700 border-slate-300" },
  [TRUST.SIMULATED]:  { label: "SIMULATED",  cls: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
};

/* Attach provenance to a record.
 *
 * Defaults are deliberately pessimistic: an object created without explicit
 * provenance is SIMULATED, from the simulation source, at zero confidence.
 * Forgetting to declare provenance therefore produces a visibly untrusted
 * object rather than a silently trusted one. */
export function withProvenance(record, provenance = {}) {
  const {
    source = SOURCE_KIND.SIMULATION,
    trust = TRUST.SIMULATED,
    confidence = null,
    owner = "InQAi POC",
    classification = CLASSIFICATION.PUBLIC,
    observedAt = null,
    updatedAt = new Date().toISOString(),
    freshnessMinutes = null,
    note = null,
  } = provenance;

  return {
    ...record,
    provenance: {
      source, trust, confidence, owner, classification,
      observedAt, updatedAt, freshnessMinutes, note,
    },
  };
}

/* Demote a record to STALE when its observation has aged past its freshness
 * window. Simulated data is exempt — it was never fresh in the first place,
 * and showing "STALE" would imply it had once been observed. */
export function applyFreshness(record, now = Date.now()) {
  const p = record?.provenance;
  if (!p || !p.freshnessMinutes || !p.observedAt) return record;
  if (p.trust === TRUST.SIMULATED) return record;

  const ageMinutes = (now - new Date(p.observedAt).getTime()) / 60000;
  if (ageMinutes <= p.freshnessMinutes) return record;

  return { ...record, provenance: { ...p, trust: TRUST.STALE } };
}

/* True when a record may inform a consequential operational decision.
 * Simulated and stale data may be displayed, but callers should not treat it
 * as grounds for action without a human in the loop. */
export function isDecisionGrade(record) {
  const t = record?.provenance?.trust;
  return t === TRUST.VERIFIED;
}

/* Does any object in this collection carry simulated data? Drives the
 * page-level DEMO banner — §14 requires the label be unmissable, not buried
 * in a tooltip. */
export function containsSimulated(collections) {
  return collections.some((items) =>
    (items || []).some((i) => i?.provenance?.trust === TRUST.SIMULATED)
  );
}

/* Human-readable "how old is this". Returns null when there is nothing
 * honest to say, so the UI can omit the field rather than print "unknown". */
export function ageLabel(record, now = Date.now()) {
  const at = record?.provenance?.observedAt || record?.provenance?.updatedAt;
  if (!at) return null;
  const mins = Math.max(0, Math.round((now - new Date(at).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} d ago`;
}
